/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  MessageSquare,
  Percent,
  Radio,
  RefreshCw,
  Sparkles,
  Users,
} from "lucide-react";
import { formatCompactNumber, formatPercent, parseDiscordList } from "../pages/discordApi";
import { getIconAmbientColor, ICON_FALLBACK_RGB } from "./discordIconColor";

export default function DiscordServerCard({
  server,
  rank,
  admin = false,
  onToggleField,
  busyField,
  onRefresh,
  refreshing = false,
}) {
  const [heroIdx, setHeroIdx] = useState(0);
  const [iconFailed, setIconFailed] = useState(false);

  const members = formatCompactNumber(server.members);
  const online = formatCompactNumber(server.online);
  const onlinePct = formatPercent(server.online_pct);
  const hasBoosts = server.boosts !== null && server.boosts !== undefined;
  const boosts = hasBoosts ? formatCompactNumber(server.boosts) : null;

  const categories = parseDiscordList(server.categories);
  const chips = [server.primary_category, ...categories]
    .filter((c, idx, arr) => c && arr.indexOf(c) === idx)
    .slice(0, 3);

  // Community names often differ from the game ("/r/DDLC" vs "Doki Doki Literature
  // Club!"), so only surface links the title doesn't already imply.
  const gameChips = useMemo(() => {
    const squash = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const name = squash(server.server_name);
    return (server.games || [])
      .filter((g) => {
        const gs = squash(g);
        if (!gs) return false;
        if (gs === name) return false;
        return !(name.length >= 4 && (name.includes(gs) || gs.includes(name)));
      })
      .slice(0, 2);
  }, [server.games, server.server_name]);

  const isHidden = admin && server.frontend_visible === false;
  const isTrackingOff = admin && server.tracking_enabled === false;
  const isReviewed = admin && server.admin_reviewed === true;
  // Reviewed + fully enabled = good (green). Reviewed but hidden/tracking-off = needs attention (red).
  const isReviewedGood = isReviewed && !isHidden && !isTrackingOff;
  const isReviewedFlagged = isReviewed && (isHidden || isTrackingOff);

  // Hero art prefers splash/banner; falls forward through candidates on load error.
  const heroCandidates = useMemo(
    () => [server.splash_url, server.banner_url].filter(Boolean),
    [server.splash_url, server.banner_url]
  );
  const richHeroSrc = heroCandidates[heroIdx] || null;
  const hasRichHero = !!richHeroSrc;
  const iconSrc = !iconFailed && server.icon_url ? server.icon_url : null;
  const iconDuplicatesHero = hasRichHero && iconSrc && richHeroSrc === iconSrc;

  // ~1 in 4 servers never set a splash/banner in Discord. Stretching and
  // blurring their (usually small, flat) icon to fill a 16:10 hero just looks
  // muddy, so that case instead shows the icon crisp and centered over an
  // ambient colour sampled from the icon itself.
  const usingIconFallback = !hasRichHero && !!iconSrc;
  const [ambientRgb, setAmbientRgb] = useState(ICON_FALLBACK_RGB);

  useEffect(() => {
    if (!usingIconFallback) return;
    let cancelled = false;
    getIconAmbientColor(iconSrc).then((rgb) => {
      if (!cancelled) setAmbientRgb(rgb);
    });
    return () => {
      cancelled = true;
    };
  }, [usingIconFallback, iconSrc]);

  return (
    <article
      className={`discord-card${isHidden ? " is-hidden" : ""}${
        isReviewedGood ? " is-reviewed-good" : ""
      }${isReviewedFlagged ? " is-reviewed-flagged" : ""}`}
    >
      <div className="discord-hero">
        {hasRichHero ? (
          <img
            className="discord-hero-img"
            src={richHeroSrc}
            alt=""
            loading="lazy"
            onError={() => setHeroIdx((i) => i + 1)}
          />
        ) : iconSrc ? (
          <div
            className="discord-hero-icon-fallback"
            style={{
              background: `radial-gradient(120% 120% at 50% 28%, rgba(${ambientRgb},0.55) 0%, rgba(10,12,14,0.95) 72%)`,
            }}
          >
            <img
              className="discord-hero-icon-fallback-img"
              src={iconSrc}
              alt=""
              loading="lazy"
              onError={() => setIconFailed(true)}
            />
          </div>
        ) : (
          <div className="discord-hero-placeholder">
            <MessageSquare size={26} />
          </div>
        )}

        <div className="discord-hero-gradient" />

        <div className="discord-hero-topline">
          {Number.isFinite(rank) && <span className="discord-rank">#{rank}</span>}
          <div className="discord-hero-topline-right">
            {(server.verified || server.partnered) && (
              <span className="discord-badge">
                {server.verified && server.partnered
                  ? "Verified · Partnered"
                  : server.verified
                  ? "Verified"
                  : "Partnered"}
              </span>
            )}
            {admin && (
              <div className="discord-admin-flags">
                {isHidden && <span className="discord-flag discord-flag-hidden">Hidden</span>}
                {isTrackingOff && <span className="discord-flag discord-flag-tracking">No Tracking</span>}
                <span className={`discord-flag ${isReviewed ? "discord-flag-reviewed" : "discord-flag-unreviewed"}`}>
                  {isReviewed ? "Reviewed" : "Unreviewed"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="discord-hero-footer">
          <div className="discord-hero-identity">
            {iconSrc && !iconDuplicatesHero && !usingIconFallback && (
              <img
                className="discord-hero-icon"
                src={iconSrc}
                alt=""
                loading="lazy"
                onError={() => setIconFailed(true)}
              />
            )}
            <span className="discord-hero-title" title={server.server_name}>
              {server.server_name || "Unknown Server"}
            </span>
          </div>

          {admin && onRefresh && (
            <button
              type="button"
              className={`discord-link-btn discord-refresh-btn${refreshing ? " is-busy" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
              disabled={refreshing}
              title="Refresh stats from Discord"
              aria-label="Refresh stats from Discord"
            >
              <RefreshCw size={13} />
            </button>
          )}
          {server.discord_url && (
            <a
              className="discord-link-btn"
              href={server.discord_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in Discord"
              aria-label="Open in Discord"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      <div className="discord-card-body">
        {server.description && <p className="discord-card-desc">{server.description}</p>}

        <div className="discord-stat-grid">
          <div className="discord-stat">
            <Users size={13} />
            <span>{members ?? "—"}</span>
            <label>Members</label>
          </div>
          <div className="discord-stat">
            <Radio size={13} />
            <span>{online ?? "—"}</span>
            <label>Online</label>
          </div>
          <div className="discord-stat">
            <Percent size={13} />
            <span>{onlinePct ?? "—"}</span>
            <label>Active</label>
          </div>
          <div className="discord-stat">
            <Sparkles size={13} />
            <span>{boosts ?? "—"}</span>
            <label>Boosts</label>
          </div>
        </div>

        {(gameChips.length > 0 || chips.length > 0) && (
          <div className="discord-chip-row">
            {gameChips.map((game) => (
              <span key={`game:${game}`} className="discord-chip discord-chip-game" title={game}>
                {game}
              </span>
            ))}
            {chips.map((chip) => (
              <span key={chip} className="discord-chip">{chip}</span>
            ))}
          </div>
        )}
      </div>

      {admin && (
        <div className="discord-admin-controls">
          <label className="discord-toggle">
            <input
              type="checkbox"
              checked={!!server.frontend_visible}
              disabled={busyField === "frontend_visible"}
              onChange={(e) => onToggleField?.("frontend_visible", e.target.checked)}
            />
            <span>Frontend Visible</span>
          </label>
          <label className="discord-toggle">
            <input
              type="checkbox"
              checked={!!server.tracking_enabled}
              disabled={busyField === "tracking_enabled"}
              onChange={(e) => onToggleField?.("tracking_enabled", e.target.checked)}
            />
            <span>Tracking Enabled</span>
          </label>
          <label className="discord-toggle">
            <input
              type="checkbox"
              checked={!!server.admin_reviewed}
              disabled={busyField === "admin_reviewed"}
              onChange={(e) => onToggleField?.("admin_reviewed", e.target.checked)}
            />
            <span>Reviewed</span>
          </label>
        </div>
      )}
    </article>
  );
}
